"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./Auth";
import ModernNavigation from "./ModernNavigation";
import ApiTestPanel from "./ApiTestPanel";
import UserManagement from "./UserManagement";
import SalesAnalytics from "./SalesAnalytics";
import AdminPanelContent from "./AdminPanelContent";
import {
  BarChart3,
  Users,
  Settings,
  Palette,
  FileText,
  Video,
  Brain,
  Code,
  Languages,
  TrendingUp,
  Target,
  Mail,
  Globe,
  Zap,
  Shield,
  Cpu,
  ChevronRight,
  Grid,
  List,
  Search,
  Bell,
  Menu,
  X,
  Rocket,
  Activity,
  Star,
  Award
} from "lucide-react";

interface Tool {
  id: string;
  name: string;
  icon: any;
  description: string;
  color: string;
  category: string;
  component?: React.ComponentType;
}

export default function UltraModernAdminPage() {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTab = localStorage.getItem('selectedTab');
    if (savedTab) {
      setSelectedTab(savedTab);
    }
  }, []);

  if (!user || user.role !== "admin") {
    return (
      <motion.div 
        className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Shield className="w-8 h-8 text-red-500" />
          </motion.div>
          <motion.h1 
            className="text-3xl font-bold text-gray-900 mb-3"
            whileHover={{ scale: 1.05 }}
          >
            Acesso Negado
          </motion.h1>
          <motion.p 
            className="text-gray-600 text-lg"
            whileHover={{ scale: 1.02 }}
          >
            Você não tem permissão para acessar esta página.
          </motion.p>
        </div>
      </motion.div>
    );
  }

  const tools: Tool[] = [
    {
      id: "image",
      name: "Geração de Imagens",
      icon: Palette,
      description: "Crie imagens impressionantes com IA",
      color: "from-blue-500 to-cyan-500",
      category: "criacao"
    },
    {
      id: "pitch",
      name: "Gerador de Pitch",
      icon: FileText,
      description: "Pitches de vendas persuasivos",
      color: "from-green-500 to-emerald-500",
      category: "criacao"
    },
    {
      id: "video",
      name: "Script de Vídeo",
      icon: Video,
      description: "Scripts profissionais para vídeos",
      color: "from-purple-500 to-pink-500",
      category: "criacao"
    },
    {
      id: "viral",
      name: "Conteúdo Viral",
      icon: TrendingUp,
      description: "Crie conteúdo com potencial viral",
      color: "from-orange-500 to-red-500",
      category: "marketing"
    },
    {
      id: "black-friday",
      name: "Black Friday",
      icon: Target,
      description: "Ofertas para Black Friday",
      color: "from-red-600 to-orange-600",
      category: "marketing"
    },
    {
      id: "email",
      name: "Email Sequence",
      icon: Mail,
      description: "Sequências de email automatizadas",
      color: "from-blue-600 to-indigo-600",
      category: "marketing"
    },
    {
      id: "funnel",
      name: "Funil de Vendas",
      icon: BarChart3,
      description: "Otimize seu processo de vendas",
      color: "from-green-600 to-teal-600",
      category: "marketing"
    },
    {
      id: "landing",
      name: "Landing Page",
      icon: Globe,
      description: "Páginas de alta conversão",
      color: "from-purple-600 to-pink-600",
      category: "marketing"
    },
    {
      id: "sentiment",
      name: "Análise de Sentimento",
      icon: Brain,
      description: "Analise emoções em textos",
      color: "from-red-500 to-orange-500",
      category: "analise"
    },
    {
      id: "osint",
      name: "OSINT",
      icon: Shield,
      description: "Inteligência de fontes abertas",
      color: "from-indigo-500 to-purple-500",
      category: "analise"
    },
    {
      id: "code",
      name: "Gerador de Código",
      icon: Code,
      description: "Gere código em múltiplas linguagens",
      color: "from-yellow-500 to-amber-500",
      category: "tecnico"
    },
    {
      id: "translate",
      name: "Tradutor",
      icon: Languages,
      description: "Traduza com contexto cultural",
      color: "from-indigo-500 to-blue-500",
      category: "tecnico"
    }
  ];

  const categories = [
    { id: "all", name: "Todas", icon: Grid },
    { id: "criacao", name: "Criação", icon: Palette },
    { id: "marketing", name: "Marketing", icon: Target },
    { id: "analise", name: "Análise", icon: Brain },
    { id: "tecnico", name: "Técnico", icon: Code }
  ];

  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === "all" || tool.category === selectedCategory;
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const renderContent = () => {
    switch (selectedTab) {
      case "overview":
        return (
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Enhanced Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Cpu, value: "15", label: "Ferramentas IA", color: "from-purple-500 to-purple-600" },
                { icon: Users, value: "2.5K", label: "Usuários Ativos", color: "from-blue-500 to-blue-600" },
                { icon: BarChart3, value: "98%", label: "Satisfação", color: "from-green-500 to-green-600" },
                { icon: Zap, value: "24/7", label: "Disponibilidade", color: "from-yellow-500 to-yellow-600" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -5,
                    boxShadow: "0 25px 50px rgba(139, 92, 246, 0.15)"
                  }}
                  className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 relative overflow-hidden"
                >
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-5`}
                    whileHover={{ opacity: 0.1 }}
                  />
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      >
                        <stat.icon className="w-10 h-10 text-gray-700" />
                      </motion.div>
                      <motion.span 
                        className="text-3xl font-bold text-gray-900"
                        whileHover={{ scale: 1.2 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      >
                        {stat.value}
                      </motion.span>
                    </div>
                    <p className="text-gray-600 font-medium">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Enhanced Tools Grid */}
            <motion.div 
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <motion.h3 
                  className="text-2xl font-bold text-gray-900"
                  whileHover={{ scale: 1.05 }}
                >
                  🚀 Ferramentas Disponíveis
                </motion.h3>
                <div className="flex items-center gap-3">
                  <motion.button
                    onClick={() => setViewMode("grid")}
                    className={`p-3 rounded-xl transition-all duration-200 ${
                      viewMode === "grid" 
                        ? "bg-purple-100 text-purple-600 shadow-lg" 
                        : "text-gray-500 hover:bg-gray-100"
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Grid className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    onClick={() => setViewMode("list")}
                    className={`p-3 rounded-xl transition-all duration-200 ${
                      viewMode === "list" 
                        ? "bg-purple-100 text-purple-600 shadow-lg" 
                        : "text-gray-500 hover:bg-gray-100"
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <List className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Enhanced Search and Filter */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.div 
                  className="flex-1 relative"
                  whileHover={{ scale: 1.02 }}
                >
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="🔍 Buscar ferramentas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                  />
                </motion.div>
                <motion.select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-medium"
                  whileHover={{ scale: 1.02 }}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </motion.select>
              </div>

              {/* Enhanced Tools Display */}
              <div className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
                <AnimatePresence>
                  {filteredTools.map((tool, index) => (
                    <motion.div
                      key={tool.id}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      onClick={() => setSelectedTab(tool.id)}
                      className={`${
                        viewMode === "grid" 
                          ? "bg-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                          : "bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer"
                      }`}
                      whileHover={{ 
                        scale: 1.03, 
                        y: -5,
                        borderColor: "#8b5cf6"
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {viewMode === "grid" ? (
                        <>
                          <motion.div 
                            className={`bg-gradient-to-r ${tool.color} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-2xl`}
                            whileHover={{ rotate: 360, scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                          >
                            <tool.icon className="w-8 h-8 text-white" />
                          </motion.div>
                          <h4 className="font-bold text-xl text-gray-900 mb-3">{tool.name}</h4>
                          <p className="text-gray-600">{tool.description}</p>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <motion.div 
                            className={`bg-gradient-to-r ${tool.color} w-12 h-12 rounded-xl flex items-center justify-center mr-6`}
                            whileHover={{ rotate: 360, scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                          >
                            <tool.icon className="w-6 h-6 text-white" />
                          </motion.div>
                          <div className="flex-1">
                            <h4 className="font-bold text-lg text-gray-900">{tool.name}</h4>
                            <p className="text-gray-600">{tool.description}</p>
                          </div>
                          <motion.div
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </motion.div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        );

      case "users":
        return <UserManagement />;
      case "analytics":
        return <SalesAnalytics />;
      case "settings":
        return <AdminPanelContent />;
      case "api-test":
        return <ApiTestPanel />;
      default:
        return (
          <motion.div 
            className="bg-white rounded-2xl shadow-xl p-12 text-center"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className={`bg-gradient-to-r ${tools.find(t => t.id === selectedTab)?.color || "from-gray-500 to-gray-600"} w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-8`}
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.5 }}
            >
              {React.createElement(tools.find(t => t.id === selectedTab)?.icon || Cpu, { className: "w-12 h-12 text-white" })}
            </motion.div>
            <motion.h3 
              className="text-3xl font-bold text-gray-900 mb-4"
              whileHover={{ scale: 1.05 }}
            >
              {tools.find(t => t.id === selectedTab)?.name || "Ferramenta"}
            </motion.h3>
            <p className="text-gray-600 text-lg mb-8">
              {tools.find(t => t.id === selectedTab)?.description || "Carregando..."}
            </p>
            <motion.div 
              className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-2xl p-8"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-4xl mb-4"
              >
                🚧
              </motion.div>
              <p className="text-yellow-800 text-lg font-medium">
                Esta ferramenta está em desenvolvimento. Em breve estará disponível!
              </p>
            </motion.div>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <ModernNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header */}
        <motion.div 
          className="mb-8 sm:mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
              <motion.h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3"
                whileHover={{ scale: 1.02 }}
              >
                🚀 Painel Administrativo
              </motion.h1>
              <motion.p 
                className="text-gray-600 text-lg"
                whileHover={{ scale: 1.02 }}
              >
                Bem-vindo, {user.name}! 👋
              </motion.p>
            </div>
            
            {/* Mobile Menu Toggle */}
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-3 bg-white rounded-xl shadow-lg border-2 border-gray-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <X key="close" className="w-6 h-6" />
                ) : (
                  <Menu key="menu" className="w-6 h-6" />
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          {/* Enhanced Desktop Navigation Tabs */}
          <div className="hidden sm:flex flex-wrap gap-3 mt-6">
            {[
              { id: "overview", name: "Visão Geral", icon: Grid },
              { id: "users", name: "Usuários", icon: Users },
              { id: "analytics", name: "Analytics", icon: BarChart3 },
              { id: "settings", name: "Configurações", icon: Settings },
              { id: "api-test", name: "Teste API", icon: Shield }
            ].map((tab, index) => (
              <motion.button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  selectedTab === tab.id
                    ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl"
                    : "bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200"
                }`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -2,
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                <tab.icon className="w-5 h-5 mr-2" />
                {tab.name}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Enhanced Mobile Navigation Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              className="sm:hidden mb-8 bg-white rounded-2xl shadow-2xl p-6 border-2 border-gray-200"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {[
                { id: "overview", name: "Visão Geral", icon: Grid },
                { id: "users", name: "Usuários", icon: Users },
                { id: "analytics", name: "Analytics", icon: BarChart3 },
                { id: "settings", name: "Configurações", icon: Settings },
                { id: "api-test", name: "Teste API", icon: Shield }
              ].map((tab, index) => (
                <motion.button
                  key={tab.id}
                  onClick={() => {
                    setSelectedTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center px-6 py-4 rounded-xl font-semibold transition-all duration-200 mb-3 ${
                    selectedTab === tab.id
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                      : "bg-gray-50 text-gray-700 hover:bg-purple-50"
                  }`}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="w-5 h-5 mr-3" />
                  {tab.name}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <motion.div 
          className="min-h-[600px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {renderContent()}
        </motion.div>
      </div>
    </div>
  );
}
